import React, { useEffect, useRef, useState } from "react";
import { Camera, X, ScanLine } from "lucide-react";

/**
 * Barcode / QR scanner using html5-qrcode. Works on iOS Safari + Android Chrome
 * (requires camera permission). Offers a manual text entry fallback for
 * estimators in low-light environments or when no rear camera is present.
 *
 * Props:
 *   - value: current decoded text (string)
 *   - onChange(decoded: string)
 *   - testid: data-testid prefix
 */
export default function BarcodeScanner({ value, onChange, testid, disabled }) {
  const [open, setOpen] = useState(false);
  const [error, setError] = useState("");
  const containerRef = useRef(null);
  const scannerRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    let cancelled = false;
    let scanner;
    (async () => {
      try {
        // Lazy-load so the lib (~150KB) doesn't bloat the main bundle
        const mod = await import("html5-qrcode");
        const { Html5Qrcode } = mod;
        if (cancelled) return;
        const elId = "barcode-scanner-" + (testid || "default");
        if (containerRef.current) containerRef.current.id = elId;
        scanner = new Html5Qrcode(elId, /* verbose */ false);
        scannerRef.current = scanner;
        await scanner.start(
          { facingMode: "environment" },
          { fps: 10, qrbox: { width: 240, height: 160 } },
          (decoded) => {
            // Stop after first decode to save battery
            onChange(decoded);
            setOpen(false);
          },
          () => {/* swallow per-frame no-match errors */},
        );
      } catch (e) {
        setError(e?.message || "Could not start camera");
      }
    })();
    return () => {
      cancelled = true;
      if (scannerRef.current) {
        scannerRef.current.stop().catch(() => {})
          .finally(() => scannerRef.current?.clear().catch(() => {}));
        scannerRef.current = null;
      }
    };
  }, [open, onChange, testid]);

  return (
    <div className="space-y-2">
      <div className="flex gap-2 items-center">
        <input
          className="qb-input flex-1"
          placeholder="Scan or type the code…"
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          disabled={disabled}
          data-testid={`${testid}-text`}
        />
        {!disabled && (
          <button
            type="button"
            onClick={() => { setError(""); setOpen(true); }}
            className="h-9 px-3 border border-brand-blue text-brand-blue hover:bg-brand-blue hover:text-white text-xs uppercase tracking-wider font-semibold inline-flex items-center gap-2"
            data-testid={`${testid}-scan-button`}
            title="Open camera to scan a barcode or QR code"
          >
            <ScanLine size={14} /> Scan
          </button>
        )}
      </div>
      {open && (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4" onClick={() => setOpen(false)}>
          <div className="bg-white rounded-lg p-3 max-w-md w-full" onClick={(e) => e.stopPropagation()} data-testid={`${testid}-scanner`}>
            <div className="flex items-center justify-between mb-2">
              <div className="inline-flex items-center gap-2 text-sm font-semibold text-slate-900">
                <Camera size={14} /> Scan a barcode or QR code
              </div>
              <button onClick={() => setOpen(false)} className="text-slate-500 hover:text-red-600">
                <X size={16} />
              </button>
            </div>
            <div ref={containerRef} className="w-full bg-black rounded overflow-hidden min-h-[260px]" />
            {error && (
              <p className="text-xs text-red-600 mt-2">{error}. Try typing the code manually instead.</p>
            )}
            <p className="text-[11px] text-slate-500 mt-2">
              Allow camera access if prompted. Hold the code 10–20 cm from the rear camera.
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
